import React from 'react';
import StepImage from './StepImage';

type SubmissionStep = {
  id: string;
  number: number;
  title: string;
  description: string;
  note?: string;
  image?: {
    src: string;
    alt: string;
  };
  cta?: {
    label: string;
    url: string;
  };
};

type SubmissionStepsProps = {
  steps: SubmissionStep[];
  portalUrl: string;
};

const SubmissionSteps: React.FC<SubmissionStepsProps> = ({ steps, portalUrl }) => {
  return (
    <div className="rounded-3xl border border-[#E7D9B8] bg-white/95 p-6 shadow-[0_22px_50px_-32px_rgba(15,23,42,0.35)]">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-slate-900">Submission steps</h2>
        <p className="text-sm text-slate-600">
          Follow the City of Toronto online screening flow and paste your letter into the Dispute
          Description field.
        </p>
      </div>

      <div className="mt-6 rounded-2xl border border-[#E7D9B8] bg-[#FFFCF5] p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#A7854F]">What you&apos;ll need</p>
        <ul className="mt-3 space-y-2 text-sm text-slate-700">
          <li className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#C6A87C]" /> Violation Notice Number
          </li>
          <li className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#C6A87C]" /> Licence Plate
          </li>
          <li className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[#C6A87C]" /> Email address (City sends decision
            via email)
          </li>
        </ul>
      </div>

      <div className="mt-6 space-y-6">
        {steps.map((step, index) => (
          <div key={step.id} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full border border-[#E7D9B8] bg-white text-sm font-semibold text-[#A7854F]">
                {step.number}
              </div>
              {index < steps.length - 1 && (
                <div className="mt-2 h-full w-px bg-[#E7D9B8]" />
              )}
            </div>

            <div className="flex-1 pb-6">
              <div className="flex flex-col gap-2">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{step.description}</p>
                  {step.note && <p className="mt-2 text-xs text-slate-500">{step.note}</p>}
                </div>

                {step.cta && (
                  <a
                    href={step.cta.url || portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-fit items-center justify-center rounded-lg bg-gradient-to-r from-[#C6A87C] to-[#A7854F] px-4 py-2 text-xs font-semibold text-white shadow-[0_10px_30px_-12px_rgba(166,133,79,0.6)] transition-transform hover:-translate-y-0.5"
                  >
                    {step.cta.label}
                  </a>
                )}

                {step.image && (
                  <StepImage src={step.image.src} alt={step.image.alt} title={step.title} />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-xs text-slate-500">
        Any information you submit may become part of the public record.
      </div>
    </div>
  );
};

export type { SubmissionStep };
export default SubmissionSteps;
